import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import ChapterEditor from "@/components/ChapterEditor";
import ModelPicker from "@/components/ModelPicker";
import CoverUpload, { resolveUrl as resolveCoverUrl } from "@/components/CoverUpload";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Download, Sparkles, Copy, Share2, FileText, Megaphone, Settings2, Eye, EyeOff, Trash2, Bell, RefreshCw, Globe, BarChart3, Rocket, Users, UserPlus, X as XIcon, ClipboardCheck, ListChecks, Activity } from "lucide-react";
import { API } from "@/lib/api";
import LanguagePicker from "@/components/LanguagePicker";
import PublishPanel from "@/components/PublishPanel";
import ReviewPanel from "@/components/ReviewPanel";
import OutlinePanel from "@/components/OutlinePanel";
import ActivityFeed from "@/components/ActivityFeed";
import { languageLabel } from "@/constants/languages";

const PLATFORMS = [
  { value: "facebook", label: "Facebook" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "linkedin", label: "LinkedIn" },
];

const TEMPLATES = {
  prelaunch: {
    facebook: "📖 Something I've been writing in secret is almost ready... {title} drops soon. Here's a peek:\n\n{description}\n\nFollow my journey here: {url}",
    whatsapp: "Hey! I'm releasing my new book \"{title}\" soon. 🎉\nRead more: {url}",
    linkedin: "After months of writing, I'm thrilled to share that my book \"{title}\" is coming soon.\n\nIt's about {theme}.\n\nFollow along: {url}",
  },
  postlaunch: {
    facebook: "🎉 \"{title}\" is officially out! If you've ever wondered about {theme}, this one's for you.\n\nGrab your copy: {url}",
    whatsapp: "My book \"{title}\" is LIVE! 🥳 Grab it here: {url}",
    linkedin: "It's official — \"{title}\" is out in the world today. A heartfelt thank you to everyone who supported this journey.\n\nRead it here: {url}",
  },
};

export default function BookEditor() {
  const { bookId } = useParams();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [activeChapter, setActiveChapter] = useState(null);
  const [tab, setTab] = useState("write");
  const [loading, setLoading] = useState(true);

  // Publicity local state
  const [social, setSocial] = useState([]);
  const [composer, setComposer] = useState({ platform: "facebook", content: "", kind: "manual" });
  const [aiTone, setAiTone] = useState("exciting");
  const [aiPhase, setAiPhase] = useState("prelaunch");
  const [aiProvider, setAiProvider] = useState("anthropic");
  const [aiBusy, setAiBusy] = useState(false);

  // Uniqueness state
  const [uniqText, setUniqText] = useState("");
  const [uniqResult, setUniqResult] = useState("");
  const [uniqBusy, setUniqBusy] = useState(false);
  const [uniqProvider, setUniqProvider] = useState("anthropic");

  // Settings local state
  const [settings, setSettings] = useState(null);

  // Subscribers + YouTube sync
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersOpen, setSubscribersOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get(`/books/${bookId}/analytics`);
      setAnalytics(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "analytics" && !analytics) loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [bRes, cRes, sRes, subsRes] = await Promise.all([
        api.get(`/books/${bookId}`),
        api.get(`/books/${bookId}/chapters`),
        api.get(`/books/${bookId}/social-posts`),
        api.get(`/books/${bookId}/subscribers`).catch(() => ({ data: { count: 0, subscribers: [] } })),
      ]);
      setBook(bRes.data);
      setChapters(cRes.data);
      setSocial(sRes.data);
      setSubscribers(subsRes.data?.subscribers || []);
      setLastSync(bRes.data.cached_videos?.last_synced_at || null);
      setSettings({
        title: bRes.data.title || "",
        slug: bRes.data.slug || "",
        theme: bRes.data.theme || "",
        genres: (bRes.data.genres || []).join(", "),
        description: bRes.data.description || "",
        short_summary: bRes.data.short_summary || "",
        language: bRes.data.language || "en",
        youtube_url: bRes.data.youtube_url || "",
        ads_url: bRes.data.ads_url || "",
        purchase_links: (bRes.data.purchase_links || []).map((l, i) => ({
          ...l,
          _uid: `pl-${Date.now()}-${i}`,
        })),
        launch_date: bRes.data.launch_date || "",
        status: bRes.data.status || "draft",
        is_public: !!bRes.data.is_public,
        isbn: bRes.data.isbn || "",
        published_date: bRes.data.published_date || "",
        buy_url: bRes.data.buy_url || "",
        youtube_api_key: bRes.data.youtube_api_key || "",
        youtube_playlist_id: bRes.data.youtube_playlist_id || "",
        youtube_channel_id: bRes.data.youtube_channel_id || "",
      });
      if (cRes.data.length > 0) setActiveChapter(cRes.data[0]);
    } catch (e) {
      toast.error("Failed to load book");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-line */ }, [bookId]);

  const addChapter = async () => {
    try {
      const { data } = await api.post(`/books/${bookId}/chapters`, {
        title: `Chapter ${chapters.length + 1}`,
        content: "",
        order: chapters.length,
      });
      setChapters([...chapters, data]);
      setActiveChapter(data);
      toast.success("Chapter added");
    } catch {
      toast.error("Failed to add chapter");
    }
  };

  const onChapterUpdated = (updated) => {
    setChapters((prev) => prev.map((c) => (c.chapter_id === updated.chapter_id ? updated : c)));
  };

  const toggleChapterPublic = async (chapter) => {
    try {
      const { data } = await api.patch(`/chapters/${chapter.chapter_id}`, { is_public: !chapter.is_public });
      onChapterUpdated(data);
      toast.success(data.is_public ? "Chapter shown on public page" : "Chapter hidden");
    } catch {
      toast.error("Update failed");
    }
  };

  const onChapterDeleted = async (ch) => {
    try {
      await api.delete(`/chapters/${ch.chapter_id}`);
      const remaining = chapters.filter((c) => c.chapter_id !== ch.chapter_id);
      setChapters(remaining);
      setActiveChapter(remaining[0] || null);
      toast.success("Chapter deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const saveSettings = async () => {
    try {
      const payload = {
        ...settings,
        genres: settings.genres.split(",").map((s) => s.trim()).filter(Boolean),
        purchase_links: (settings.purchase_links || [])
          .filter((l) => (l.label || "").trim() && (l.url || "").trim())
          .map((l) => ({ label: l.label.trim(), url: l.url.trim() })),
      };
      const { data } = await api.patch(`/books/${bookId}`, payload);
      setBook(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const downloadExport = (fmt) => {
    const token = localStorage.getItem("bf_token");
    const url = `${API}/books/${bookId}/export/${fmt}?_=${Date.now()}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("Failed");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${book.slug}.${fmt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => toast.error("Export failed"));
  };

  const runUniqueness = async () => {
    if (!uniqText.trim()) return toast.error("Paste some text first");
    setUniqBusy(true);
    setUniqResult("");
    try {
      const { data } = await api.post("/ai/uniqueness", { text: uniqText, provider: uniqProvider });
      setUniqResult(data.result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Analysis failed");
    } finally {
      setUniqBusy(false);
    }
  };

  const publicUrl = useMemo(() => {
    if (!user || !book) return "";
    return `${window.location.origin}/${user.slug}/${book.slug}`;
  }, [user, book]);

  const fillTemplate = (tmpl) => {
    if (!book) return "";
    return tmpl
      .replaceAll("{title}", book.title || "")
      .replaceAll("{theme}", book.theme || "")
      .replaceAll("{description}", book.description || "")
      .replaceAll("{url}", publicUrl);
  };

  const useTemplate = () => {
    const t = TEMPLATES[aiPhase]?.[composer.platform];
    if (!t) return;
    setComposer({ ...composer, content: fillTemplate(t), kind: "template" });
  };

  const generateAiPost = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/social", {
        book_id: bookId,
        platform: composer.platform,
        tone: aiTone,
        phase: aiPhase,
        provider: aiProvider,
        language: book?.language || "en",
      });
      setComposer({ ...composer, content: data.result, kind: "ai" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const saveSocialPost = async () => {
    if (!composer.content.trim()) return toast.error("Write or generate content first");
    try {
      const { data } = await api.post(`/books/${bookId}/social-posts`, composer);
      setSocial([data, ...social]);
      setComposer({ platform: composer.platform, content: "", kind: "manual" });
      toast.success("Saved to your post library");
    } catch {
      toast.error("Failed to save");
    }
  };

  const deletePost = async (postId) => {
    try {
      await api.delete(`/social-posts/${postId}`);
      setSocial(social.filter((s) => s.post_id !== postId));
    } catch {
      toast.error("Failed");
    }
  };

  const resyncVideos = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/books/${bookId}/videos/sync`);
      if (data.configured) {
        setLastSync(data.last_synced_at);
        toast.success(`Pulled ${data.videos?.length || 0} video(s) from YouTube`);
      } else {
        toast.error("Add a YouTube key + playlist/channel in Settings first");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const emailEveryone = async () => {
    try {
      const { data } = await api.get(`/books/${bookId}/subscribers/mailto`);
      if (!data.count) {
        toast.error("No subscribers yet");
        return;
      }
      window.location.href = data.mailto;
    } catch {
      toast.error("Could not build the email draft");
    }
  };

  const removeSubscriber = async (sub) => {
    if (!confirm(`Remove ${sub.email}?`)) return;
    try {
      await api.delete(`/books/${bookId}/subscribers/${sub.subscriber_id}`);
      setSubscribers(subscribers.filter((s) => s.subscriber_id !== sub.subscriber_id));
    } catch {
      toast.error("Failed");
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  if (loading || !book || !settings) {
    return (
      <div className="min-h-screen" style={{ background: "var(--bf-bg-app)" }}>
        <SiteNav />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-sm" style={{ color: "var(--bf-text-muted)" }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg-app)" }} data-testid="book-editor-page">
      <SiteNav />
      <header
        className="border-b"
        style={{ borderColor: "var(--bf-border)", background: "var(--bf-bg)" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <Link to="/dashboard" className="label-eyebrow hover:underline" data-testid="back-to-dashboard">← All books</Link>
          <div className="flex items-start gap-5 mt-3">
            {book.cover_image_url && (
              <img
                src={resolveCoverUrl(book.cover_image_url)}
                alt="cover"
                className="w-16 h-24 object-cover rounded"
                style={{ border: "1px solid var(--bf-border)" }}
                data-testid="book-header-cover"
              />
            )}
            <div>
              <h1 className="font-serif text-4xl" data-testid="book-editor-title">{book.title}</h1>
              {book.theme && <p className="mt-1 italic" style={{ color: "var(--bf-text-muted)" }}>{book.theme}</p>}
            </div>
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <TabsList className="mb-10 bg-transparent border-b w-full justify-start rounded-none p-0 h-auto gap-6" style={{ borderColor: "var(--bf-border)" }}>
          <TabsTrigger value="outline" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-outline">
            <ListChecks className="w-4 h-4 mr-2" /> Outline
          </TabsTrigger>
          <TabsTrigger value="write" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-write">
            <FileText className="w-4 h-4 mr-2" /> Write
          </TabsTrigger>
          <TabsTrigger value="uniqueness" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-uniqueness">
            <Sparkles className="w-4 h-4 mr-2" /> Uniqueness
          </TabsTrigger>
          <TabsTrigger value="manuscript" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-manuscript">
            <Download className="w-4 h-4 mr-2" /> Manuscript
          </TabsTrigger>
          <TabsTrigger value="publicity" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-publicity">
            <Megaphone className="w-4 h-4 mr-2" /> Publicity
          </TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-analytics">
            <BarChart3 className="w-4 h-4 mr-2" /> Analytics
          </TabsTrigger>
          <TabsTrigger value="review" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-review">
            <ClipboardCheck className="w-4 h-4 mr-2" /> Review
          </TabsTrigger>
          <TabsTrigger value="activity" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-activity">
            <Activity className="w-4 h-4 mr-2" /> Activity
          </TabsTrigger>
          <TabsTrigger value="publish" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-publish">
            <Rocket className="w-4 h-4 mr-2" /> Publish
          </TabsTrigger>
          <TabsTrigger value="settings" className="rounded-none data-[state=active]:shadow-none data-[state=active]:border-b-2 px-1 pb-3 data-[state=active]:border-[color:var(--bf-primary)]" data-testid="tab-settings">
            <Settings2 className="w-4 h-4 mr-2" /> Settings
          </TabsTrigger>
        </TabsList>

        {/* WRITE TAB */}
        {/* OUTLINE TAB */}
        <TabsContent value="outline" className="mt-0">
          <OutlinePanel
            bookId={bookId}
            book={book}
            onChaptersGenerated={() => loadAll && loadAll()}
          />
        </TabsContent>

        <TabsContent value="write" className="mt-0">
          <div className="grid lg:grid-cols-[240px_1fr] gap-10">
            <aside data-testid="chapter-list">
              <div className="flex items-center justify-between mb-4">
                <h3 className="label-eyebrow">Chapters</h3>
                <Button size="sm" variant="ghost" onClick={addChapter} data-testid="add-chapter-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <ul className="space-y-1">
                {chapters.map((c, i) => (
                  <li key={c.chapter_id}>
                    <div
                      className={`flex items-center gap-1 rounded transition-colors ${activeChapter?.chapter_id === c.chapter_id ? "" : "hover:bg-[color:var(--bf-surface)]"}`}
                      style={{
                        background: activeChapter?.chapter_id === c.chapter_id ? "var(--bf-surface)" : "transparent",
                      }}
                    >
                      <button
                        onClick={() => setActiveChapter(c)}
                        className="flex-1 text-left text-sm px-3 py-2"
                        style={{ fontWeight: activeChapter?.chapter_id === c.chapter_id ? 600 : 400 }}
                        data-testid={`chapter-list-item-${i}`}
                      >
                        <span className="block truncate">{c.title || `Chapter ${i + 1}`}</span>
                        <span className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                          {c.word_count} words {c.is_public ? "• shown on page" : ""}
                        </span>
                      </button>
                      <button
                        onClick={() => toggleChapterPublic(c)}
                        className="p-2 mr-1 rounded transition-colors hover:bg-white"
                        title={c.is_public ? "Hide from public book page" : "Show on public book page"}
                        data-testid={`chapter-public-toggle-${i}`}
                        style={{ color: c.is_public ? "var(--bf-primary)" : "var(--bf-text-muted)" }}
                      >
                        {c.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              {chapters.length === 0 && (
                <Button onClick={addChapter} variant="outline" className="w-full mt-3" data-testid="empty-add-chapter-btn">
                  <Plus className="w-4 h-4 mr-2" /> First chapter
                </Button>
              )}
              {chapters.some((c) => c.is_public) && (
                <p className="text-xs mt-4 px-1" style={{ color: "var(--bf-text-muted)" }} data-testid="public-chapters-hint">
                  {chapters.filter((c) => c.is_public).length} chapter(s) shown on your public book page.
                </p>
              )}
            </aside>
            <main>
              {activeChapter ? (
                <ChapterEditor
                  chapter={activeChapter}
                  bookId={bookId}
                  bookLanguage={book?.language || "en"}
                  onUpdated={onChapterUpdated}
                  onDeleted={onChapterDeleted}
                />
              ) : (
                <div className="text-center py-24" style={{ color: "var(--bf-text-muted)" }} data-testid="no-chapter-empty">
                  <p className="font-serif text-2xl mb-2">No chapters yet</p>
                  <p className="text-sm mb-6">Create your first chapter and start writing.</p>
                  <Button onClick={addChapter} className="text-white" style={{ background: "var(--bf-primary)" }}>
                    Add first chapter
                  </Button>
                </div>
              )}
            </main>
          </div>
        </TabsContent>

        {/* UNIQUENESS TAB */}
        <TabsContent value="uniqueness" className="mt-0">
          <div className="grid lg:grid-cols-2 gap-10" data-testid="uniqueness-panel">
            <div>
              <h2 className="font-serif text-3xl mb-2">Originality analysis</h2>
              <p className="text-sm mb-6" style={{ color: "var(--bf-text-muted)" }}>
                Paste any section (up to ~6,000 chars) — we'll score it for clichés, voice and possible overlap with known works.
              </p>
              <div className="mb-3"><ModelPicker value={uniqProvider} onChange={setUniqProvider} /></div>
              <Textarea
                rows={14}
                value={uniqText}
                onChange={(e) => setUniqText(e.target.value)}
                placeholder="Paste passages from your manuscript…"
                data-testid="uniqueness-input"
              />
              <div className="flex gap-3 mt-4">
                <Button
                  onClick={runUniqueness} disabled={uniqBusy}
                  className="text-white" style={{ background: "var(--bf-primary)" }}
                  data-testid="uniqueness-run-btn"
                >
                  {uniqBusy ? "Analysing…" : "Run analysis"}
                </Button>
                {activeChapter && (
                  <Button variant="outline" onClick={() => setUniqText(activeChapter.content || "")} data-testid="uniqueness-load-chapter-btn">
                    Load current chapter
                  </Button>
                )}
              </div>
            </div>
            <div
              className="p-6 rounded-lg"
              style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)", minHeight: "60vh" }}
              data-testid="uniqueness-result"
            >
              {uniqResult ? (
                <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">{uniqResult}</pre>
              ) : (
                <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>The report will appear here.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* MANUSCRIPT TAB */}
        <TabsContent value="manuscript" className="mt-0">
          <div className="max-w-3xl" data-testid="manuscript-panel">
            <h2 className="font-serif text-3xl mb-2">Export your manuscript</h2>
            <p className="text-sm mb-8" style={{ color: "var(--bf-text-muted)" }}>
              We compile every chapter into a clean, typeset document. Both formats are editor-ready.
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="p-7 rounded-lg" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}>
                <h3 className="font-serif text-2xl mb-2">PDF</h3>
                <p className="text-sm mb-5" style={{ color: "var(--bf-text-muted)" }}>Print-ready with title page, chapter breaks and Times typography.</p>
                <Button onClick={() => downloadExport("pdf")} className="text-white w-full" style={{ background: "var(--bf-primary)" }} data-testid="export-pdf-btn">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
              <div className="p-7 rounded-lg" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}>
                <h3 className="font-serif text-2xl mb-2">DOCX</h3>
                <p className="text-sm mb-5" style={{ color: "var(--bf-text-muted)" }}>Editable Word document for working with editors and beta readers.</p>
                <Button onClick={() => downloadExport("docx")} variant="outline" className="w-full" data-testid="export-docx-btn">
                  <Download className="w-4 h-4 mr-2" /> Download DOCX
                </Button>
              </div>
            </div>
            <div className="mt-10 p-5 rounded-lg" style={{ border: "1px solid var(--bf-border)" }}>
              <div className="label-eyebrow mb-2">Manuscript summary</div>
              <div className="text-sm flex flex-wrap gap-x-8 gap-y-2">
                <span><strong>{chapters.length}</strong> chapters</span>
                <span><strong>{chapters.reduce((a, c) => a + (c.word_count || 0), 0).toLocaleString()}</strong> words</span>
                <span><strong>{book.status}</strong></span>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* PUBLICITY TAB */}
        <TabsContent value="publicity" className="mt-0">
          <div className="grid lg:grid-cols-3 gap-10" data-testid="publicity-panel">
            <section className="lg:col-span-2">
              <h2 className="font-serif text-3xl mb-2">Compose a post</h2>
              <p className="text-sm mb-6" style={{ color: "var(--bf-text-muted)" }}>
                Generate with AI, start from a template, or write your own. Save to your library when ready.
              </p>

              <div className="grid sm:grid-cols-4 gap-3 mb-4">
                <select
                  className="h-10 rounded-md border px-3 text-sm"
                  style={{ borderColor: "var(--bf-border)", background: "white" }}
                  value={composer.platform}
                  onChange={(e) => setComposer({ ...composer, platform: e.target.value })}
                  data-testid="publicity-platform"
                >
                  {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <select
                  className="h-10 rounded-md border px-3 text-sm"
                  style={{ borderColor: "var(--bf-border)", background: "white" }}
                  value={aiPhase} onChange={(e) => setAiPhase(e.target.value)}
                  data-testid="publicity-phase"
                >
                  <option value="prelaunch">Pre-launch</option>
                  <option value="postlaunch">Post-launch</option>
                </select>
                <select
                  className="h-10 rounded-md border px-3 text-sm"
                  style={{ borderColor: "var(--bf-border)", background: "white" }}
                  value={aiTone} onChange={(e) => setAiTone(e.target.value)}
                  data-testid="publicity-tone"
                >
                  <option value="exciting">Exciting</option>
                  <option value="warm">Warm</option>
                  <option value="professional">Professional</option>
                  <option value="mysterious">Mysterious</option>
                </select>
                <ModelPicker value={aiProvider} onChange={setAiProvider} />
              </div>

              <Textarea
                rows={10}
                value={composer.content}
                onChange={(e) => setComposer({ ...composer, content: e.target.value, kind: "manual" })}
                placeholder="Your post text…"
                data-testid="publicity-content"
              />

              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={generateAiPost} disabled={aiBusy} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="publicity-ai-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> {aiBusy ? "Drafting…" : "Generate with AI"}
                </Button>
                <Button onClick={useTemplate} variant="outline" data-testid="publicity-template-btn">
                  Use template
                </Button>
                <Button onClick={saveSocialPost} variant="secondary" data-testid="publicity-save-btn">
                  Save to library
                </Button>
                <Button onClick={() => copyText(composer.content)} variant="ghost" data-testid="publicity-copy-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
              </div>
            </section>

            <aside className="space-y-6">
              <div
                className="p-5 rounded-lg"
                style={{ background: "var(--bf-primary)", border: "1px solid var(--bf-border)", color: "white" }}
                data-testid="publicity-subscribers-card"
              >
                <div className="label-eyebrow mb-2" style={{ color: "rgba(255,255,255,0.75)" }}>Launch list</div>
                <p className="font-serif text-4xl" data-testid="publicity-subscribers-count" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {subscribers.length}
                </p>
                <p className="text-xs mb-4 opacity-90">
                  reader{subscribers.length === 1 ? "" : "s"} signed up on your book page.
                </p>
                <Button
                  onClick={emailEveryone}
                  disabled={subscribers.length === 0}
                  size="sm"
                  className="w-full"
                  style={{ background: "white", color: "var(--bf-primary)" }}
                  data-testid="publicity-email-everyone-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Email everyone (open draft)
                </Button>
                {subscribers.length > 0 && (
                  <button
                    onClick={() => setSubscribersOpen(!subscribersOpen)}
                    className="text-xs mt-3 underline opacity-90 hover:opacity-100"
                    data-testid="publicity-subscribers-toggle"
                  >
                    {subscribersOpen ? "Hide" : "See"} list ({subscribers.length})
                  </button>
                )}
                {subscribersOpen && (
                  <ul
                    className="mt-3 space-y-1 max-h-56 overflow-auto rounded p-2 text-xs"
                    style={{ background: "rgba(255,255,255,0.12)" }}
                    data-testid="publicity-subscribers-list"
                  >
                    {subscribers.map((s) => (
                      <li
                        key={s.subscriber_id}
                        className="flex justify-between items-center gap-2 py-1"
                        data-testid={`subscriber-${s.subscriber_id}`}
                      >
                        <span className="truncate">{s.email}</span>
                        <button
                          onClick={() => removeSubscriber(s)}
                          className="text-xs underline opacity-70 hover:opacity-100"
                          data-testid={`subscriber-remove-${s.subscriber_id}`}
                        >
                          remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="p-5 rounded-lg" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }} data-testid="public-share-card">
                <div className="label-eyebrow mb-2">Public book page</div>
                <p className="text-sm break-all mb-3" style={{ color: "var(--bf-text-muted)" }} data-testid="public-share-url">
                  {publicUrl}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyText(publicUrl)} data-testid="share-copy-btn">
                    <Copy className="w-4 h-4 mr-1" /> Copy link
                  </Button>
                  <a href={`/${user.slug}/${book.slug}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="ghost" data-testid="share-open-btn">
                      <Share2 className="w-4 h-4 mr-1" /> Open
                    </Button>
                  </a>
                </div>
                {!book.is_public && (
                  <p className="text-xs mt-3" style={{ color: "var(--bf-primary)" }}>
                    Tip: Turn on "Show on my author landing page" in Settings to feature this book at /{user.slug}.
                  </p>
                )}
              </div>

              <div>
                <h3 className="label-eyebrow mb-3">Saved posts</h3>
                <ul className="space-y-3" data-testid="publicity-saved-list">
                  {social.length === 0 && (
                    <li className="text-sm" style={{ color: "var(--bf-text-muted)" }}>None yet.</li>
                  )}
                  {social.map((p) => (
                    <li key={p.post_id} className="p-3 rounded text-sm" style={{ background: "white", border: "1px solid var(--bf-border)" }}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="label-eyebrow">{p.platform}</span>
                        <div className="flex gap-2">
                          <button onClick={() => copyText(p.content)} className="text-xs hover:underline" data-testid={`copy-saved-${p.post_id}`}>copy</button>
                          <button onClick={() => deletePost(p.post_id)} className="text-xs hover:underline" data-testid={`delete-saved-${p.post_id}`}>delete</button>
                        </div>
                      </div>
                      <p className="whitespace-pre-wrap line-clamp-4">{p.content}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </TabsContent>

        {/* ANALYTICS TAB */}
        <TabsContent value="analytics" className="mt-0">
          <div className="max-w-4xl space-y-8" data-testid="analytics-panel">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-serif text-3xl mb-1">Page analytics</h2>
                <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
                  How many readers have visited <code className="text-xs">/{user?.slug}/{book?.slug}</code> on this book.
                </p>
              </div>
              <Button variant="outline" onClick={loadAnalytics} disabled={analyticsLoading} data-testid="analytics-refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${analyticsLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            {!analytics && analyticsLoading && (
              <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="analytics-loading">Loading…</p>
            )}

            {analytics && (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-cards">
                  {[
                    ["Total views", analytics.total_views, "since the page went live"],
                    ["Unique visitors", analytics.unique_visitors, "by IP + browser"],
                    ["Last 30 days", analytics.views_last_30d, `${analytics.unique_last_30d} unique`],
                    ["This week (peak)", Math.max(...analytics.last_7_days.map((d) => d.views)), "highest day"],
                  ].map(([label, value, hint]) => (
                    <div
                      key={label}
                      className="p-5 rounded-lg"
                      style={{ background: "white", border: "1px solid var(--bf-border)" }}
                      data-testid={`analytics-card-${label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <div className="label-eyebrow mb-2">{label}</div>
                      <div className="font-serif text-4xl" style={{ fontVariantNumeric: "tabular-nums", fontFamily: "Work Sans, sans-serif", fontWeight: 600 }}>
                        {value}
                      </div>
                      <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>{hint}</p>
                    </div>
                  ))}
                </div>

                <div data-testid="analytics-last-7-days">
                  <div className="label-eyebrow mb-3">Last 7 days</div>
                  <div className="space-y-2">
                    {(() => {
                      const max = Math.max(1, ...analytics.last_7_days.map((d) => d.views));
                      return analytics.last_7_days.map((d) => (
                        <div key={d.day} className="grid grid-cols-[80px_1fr_40px] items-center gap-3 text-sm">
                          <span style={{ color: "var(--bf-text-muted)" }}>
                            {new Date(d.day).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                          <div className="h-3 rounded-full" style={{ background: "var(--bf-surface)" }}>
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${(d.views / max) * 100}%`, background: "var(--bf-primary)" }}
                            />
                          </div>
                          <span className="text-right tabular-nums">{d.views}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </>
            )}
          </div>
        </TabsContent>



        {/* REVIEW TAB */}
        <TabsContent value="review" className="mt-0">
          <ReviewPanel bookId={bookId} />
        </TabsContent>

        {/* ACTIVITY TAB */}
        <TabsContent value="activity" className="mt-0">
          <ActivityFeed bookId={bookId} />
        </TabsContent>

        {/* PUBLISH TAB */}
        <TabsContent value="publish" className="mt-0">
          <PublishPanel bookId={bookId} book={book} />
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings" className="mt-0">
          <div className="max-w-2xl space-y-5" data-testid="settings-panel">
            <h2 className="font-serif text-3xl mb-4">Book settings</h2>
            <CoverUpload
              bookId={bookId}
              coverUrl={book.cover_image_url}
              onUploaded={(url) => setBook({ ...book, cover_image_url: url })}
            />
            <div>
              <Label className="mb-1.5 block">Title</Label>
              <Input value={settings.title} onChange={(e) => setSettings({ ...settings, title: e.target.value })} data-testid="settings-title" />
            </div>
            <div data-testid="settings-slug-block">
              <Label className="mb-1.5 block">Custom URL slug</Label>
              <div className="flex gap-2 items-center">
                <span className="text-sm" style={{ color: "var(--bf-text-muted)" }}>/{user?.slug}/</span>
                <Input
                  value={settings.slug || book.slug}
                  onChange={(e) => setSettings({ ...settings, slug: e.target.value.toLowerCase() })}
                  placeholder="my-book"
                  className="flex-1"
                  data-testid="settings-book-slug"
                />
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>
                Lowercase letters, numbers, hyphens. Changes the public URL of this book.
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block">Theme</Label>
              <Input value={settings.theme} onChange={(e) => setSettings({ ...settings, theme: e.target.value })} data-testid="settings-theme" />
            </div>
            <div>
              <Label className="mb-1.5 block">Genres (comma separated)</Label>
              <Input value={settings.genres} onChange={(e) => setSettings({ ...settings, genres: e.target.value })} data-testid="settings-genres" />
            </div>
            <div>
              <Label className="mb-1.5 flex items-center gap-2"><Globe className="w-4 h-4" /> Primary language</Label>
              <LanguagePicker
                value={settings.language}
                onChange={(v) => setSettings({ ...settings, language: v })}
                triggerClassName="w-full"
                testid="settings-language"
              />
              <p className="text-xs mt-1.5" style={{ color: "var(--bf-text-muted)" }}>
                Used by AI to generate chapters and social posts. Shown on your public book page.
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block">Description</Label>
              <Textarea rows={3} value={settings.description} onChange={(e) => setSettings({ ...settings, description: e.target.value })} data-testid="settings-description" />
            </div>
            <div>
              <Label className="mb-1.5 block">Short summary <span className="text-xs font-normal" style={{ color: "var(--bf-text-muted)" }}>(shown on your author landing page — keep it punchy)</span></Label>
              <Textarea
                rows={3}
                placeholder="A grieving cartographer's daughter inherits a map that may lead to her father's killer — or her own undoing."
                value={settings.short_summary}
                onChange={(e) => setSettings({ ...settings, short_summary: e.target.value })}
                data-testid="settings-short-summary"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">ISBN</Label>
                <Input value={settings.isbn} onChange={(e) => setSettings({ ...settings, isbn: e.target.value })} placeholder="978-3-16-148410-0" data-testid="settings-isbn" />
              </div>
              <div>
                <Label className="mb-1.5 block">Published date</Label>
                <Input type="date" value={settings.published_date} onChange={(e) => setSettings({ ...settings, published_date: e.target.value })} data-testid="settings-published-date" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">"Book your copy" URL <span className="text-xs font-normal" style={{ color: "var(--bf-text-muted)" }}>(primary buy button on the landing card)</span></Label>
              <Input value={settings.buy_url} onChange={(e) => setSettings({ ...settings, buy_url: e.target.value })} placeholder="https://amazon.com/dp/…" data-testid="settings-buy-url" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">YouTube trailer URL</Label>
                <Input value={settings.youtube_url} onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })} placeholder="https://youtu.be/…" data-testid="settings-youtube" />
              </div>
              <div>
                <Label className="mb-1.5 block">Featured ad / CTA URL</Label>
                <Input value={settings.ads_url} onChange={(e) => setSettings({ ...settings, ads_url: e.target.value })} placeholder="https://…" data-testid="settings-ads" />
              </div>
            </div>

            <div className="space-y-3" data-testid="settings-purchase-links">
              <Label className="block">Purchase / read links</Label>
              <p className="text-xs -mt-1" style={{ color: "var(--bf-text-muted)" }}>
                Where can readers buy or read your book? Add as many retailers as you like.
              </p>
              {(settings.purchase_links || []).map((link, idx) => (
                <div key={link._uid || `pl-fallback-${idx}`} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center" data-testid={`purchase-link-row-${idx}`}>
                  <Input
                    placeholder="Retailer (e.g. Amazon)"
                    value={link.label || ""}
                    onChange={(e) => {
                      const next = [...settings.purchase_links];
                      next[idx] = { ...next[idx], label: e.target.value };
                      setSettings({ ...settings, purchase_links: next });
                    }}
                    data-testid={`purchase-link-label-${idx}`}
                  />
                  <Input
                    placeholder="https://amazon.com/…"
                    value={link.url || ""}
                    onChange={(e) => {
                      const next = [...settings.purchase_links];
                      next[idx] = { ...next[idx], url: e.target.value };
                      setSettings({ ...settings, purchase_links: next });
                    }}
                    data-testid={`purchase-link-url-${idx}`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      const next = settings.purchase_links.filter((_, i) => i !== idx);
                      setSettings({ ...settings, purchase_links: next });
                    }}
                    data-testid={`purchase-link-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setSettings({
                    ...settings,
                    purchase_links: [
                      ...(settings.purchase_links || []),
                      { label: "", url: "", _uid: `pl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
                    ],
                  })
                }
                data-testid="purchase-link-add-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add purchase link
              </Button>
            </div>

            <div className="space-y-3 pt-2 border-t" style={{ borderColor: "var(--bf-border)" }} data-testid="settings-youtube-section">
              <Label className="block pt-4">Pre-launch / launch videos</Label>
              <p className="text-xs -mt-1" style={{ color: "var(--bf-text-muted)" }}>
                Paste a YouTube <strong>Data API v3 key</strong> (it starts with <code>AIza…</code>) plus a playlist OR a channel ID. Videos appear on the public book page, sorted by date and grouped by language.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block text-xs">YouTube Data API key</Label>
                  <Input
                    type="password"
                    value={settings.youtube_api_key}
                    onChange={(e) => setSettings({ ...settings, youtube_api_key: e.target.value })}
                    placeholder="AIzaSy…"
                    data-testid="settings-youtube-api-key"
                  />
                  {settings.youtube_api_key && !settings.youtube_api_key.startsWith("AIza") && (
                    <p className="text-xs mt-1.5" style={{ color: "var(--bf-primary)" }} data-testid="settings-yt-key-warning">
                      ⚠ That doesn't look like a Data API key. Real keys start with <code>AIza…</code>. If yours ends in <code>.apps.googleusercontent.com</code>, that's an OAuth Client ID — you need a different credential type.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs">Playlist ID <span className="font-normal opacity-70">(or full playlist URL)</span></Label>
                  <Input
                    value={settings.youtube_playlist_id}
                    onChange={(e) => setSettings({ ...settings, youtube_playlist_id: e.target.value })}
                    placeholder="PLxxxxxx or paste full URL"
                    data-testid="settings-youtube-playlist"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs">— or Channel ID <span className="font-normal opacity-70">(starts with UC…)</span></Label>
                  <Input
                    value={settings.youtube_channel_id}
                    onChange={(e) => setSettings({ ...settings, youtube_channel_id: e.target.value })}
                    placeholder="UCxxxxxxxxxxxxxxxxxx"
                    data-testid="settings-youtube-channel"
                  />
                </div>
              </div>
              <div className="text-xs space-y-1" style={{ color: "var(--bf-text-muted)" }}>
                <p>
                  Need a key?{" "}
                  <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline">
                    Google Cloud Console → Credentials → Create credentials → API key
                  </a>
                </p>
                <p>
                  Then enable{" "}
                  <a href="https://console.cloud.google.com/apis/library/youtube.googleapis.com" target="_blank" rel="noreferrer" className="underline">
                    YouTube Data API v3
                  </a>{" "}
                  on the same project.
                </p>
                <p>Your key is stored privately and never exposed on the public page.</p>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2">
                <p className="text-xs" style={{ color: "var(--bf-text-muted)" }} data-testid="settings-yt-last-sync">
                  {lastSync ? `Last synced ${new Date(lastSync).toLocaleString()}` : "Not yet synced"}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resyncVideos}
                  disabled={syncing}
                  data-testid="settings-yt-resync-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing…" : "Re-sync videos now"}
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Launch date</Label>
                <Input type="date" value={settings.launch_date || ""} onChange={(e) => setSettings({ ...settings, launch_date: e.target.value })} data-testid="settings-launch-date" />
              </div>
              <div>
                <Label className="mb-1.5 block">Status</Label>
                <select
                  className="h-10 w-full rounded-md border px-3 text-sm"
                  style={{ borderColor: "var(--bf-border)", background: "white" }}
                  value={settings.status}
                  onChange={(e) => setSettings({ ...settings, status: e.target.value })}
                  data-testid="settings-status"
                >
                  <option value="draft">Draft</option>
                  <option value="prelaunch">Pre-launch</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 rounded" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}>
                <div className="pr-4">
                  <div className="font-serif text-lg">Show on my author landing page</div>
                  <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                    When on, this book is listed at <strong>/{user.slug}</strong>. Direct link <strong>/{user.slug}/{book.slug}</strong> always works regardless.
                  </p>
                </div>
                <Switch
                  checked={settings.is_public}
                  onCheckedChange={(v) => setSettings({ ...settings, is_public: v })}
                  data-testid="settings-public-toggle"
                />
              </div>
              <Link to={`/${user.slug}/${book.slug}`} target="_blank" data-testid="settings-public-link" className="inline-block">
                <Button variant="outline" size="sm">Open public book page</Button>
              </Link>
            </div>
            <Button onClick={saveSettings} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="settings-save-btn">
              Save settings
            </Button>

            {/* Collaborators */}
            <CollaboratorsSection bookId={bookId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CollaboratorsSection({ bookId }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/books/${bookId}/collaborators`);
      setRows(data);
    } catch (err) {
      // Viewer/non-collaborator gets 403 here — expected. Log at debug only so the
      // panel renders empty without an error toast.
      console.debug("collaborator list skipped", err?.response?.status || err?.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [bookId]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/collaborators`, { email, role });
      setRows([data, ...rows]);
      const fullUrl = `${window.location.origin}${data.invite_url}`;
      setInviteUrl(fullUrl);
      await navigator.clipboard.writeText(fullUrl).catch(() => {});
      toast.success("Invite created — link copied to clipboard");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this collaborator?")) return;
    try {
      await api.delete(`/books/${bookId}/collaborators/${id}`);
      setRows(rows.filter((r) => r.collab_id !== id));
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <section className="pt-8 border-t" style={{ borderColor: "var(--bf-border)" }} data-testid="collaborators-section">
      <h3 className="font-serif text-2xl mb-3 flex items-center gap-2"><Users className="w-5 h-5" /> Co-authors</h3>
      <p className="text-xs mb-4" style={{ color: "var(--bf-text-muted)" }}>
        Invite by email. Editors can read + write chapters. Viewers read only. Only the owner can change settings or delete the book.
      </p>
      <form onSubmit={invite} className="grid grid-cols-[1fr_120px_auto] gap-2 mb-4" data-testid="collaborators-invite-form">
        <Input
          type="email"
          required
          placeholder="co-author@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="collab-invite-email"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="border rounded px-2 text-sm"
          style={{ borderColor: "var(--bf-border)" }}
          data-testid="collab-invite-role"
        >
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
        <Button type="submit" disabled={busy} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="collab-invite-submit">
          <UserPlus className="w-4 h-4 mr-1.5" /> {busy ? "Inviting…" : "Invite"}
        </Button>
      </form>
      {inviteUrl && (
        <p className="text-xs mb-4 p-3 rounded" style={{ background: "var(--bf-surface)", color: "var(--bf-text-muted)" }} data-testid="collab-invite-url">
          Share this link: <code className="break-all">{inviteUrl}</code>
        </p>
      )}
      <ul className="space-y-2" data-testid="collaborators-list">
        {rows.length === 0 && (
          <li className="text-sm py-3" style={{ color: "var(--bf-text-muted)" }} data-testid="collaborators-empty">
            No collaborators yet.
          </li>
        )}
        {rows.map((r) => (
          <li
            key={r.collab_id}
            className="flex items-center justify-between p-3 rounded"
            style={{ background: "white", border: "1px solid var(--bf-border)" }}
            data-testid={`collaborator-${r.collab_id}`}
          >
            <div>
              <p className="text-sm">{r.invitee_name || r.invitee_email}</p>
              <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                {r.role} · {r.status} {r.status === "pending" && r.invited_at ? `(invited ${new Date(r.invited_at).toLocaleDateString()})` : ""}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(r.collab_id)} data-testid={`collab-remove-${r.collab_id}`}>
              <XIcon className="w-4 h-4" />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
